import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocTitle } from '../hooks/useDocTitle';
import { games } from '../data/games';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';
import Footer from '../sections/Footer';

const gridScr = keyframes`0%{background-position:0 0}100%{background-position:0 60px}`;
const fadeUp  = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;
const blink   = keyframes`0%,100%{opacity:1}50%{opacity:.25}`;
const scanLine= keyframes`0%{transform:translateY(-100%);opacity:0}10%{opacity:.4}90%{opacity:.4}100%{transform:translateY(400%);opacity:0}`;

const Page = styled.div`
  min-height:100vh;overflow-x:hidden;background:${({theme})=>theme.colors.darker};padding-top:72px;
`;
const GridBg = styled.div`
  position:fixed;inset:0;pointer-events:none;z-index:0;
  background-image:linear-gradient(rgba(0,163,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(0,163,255,.025) 1px,transparent 1px);
  background-size:60px 60px;animation:${gridScr} 22s linear infinite;
`;

const Hero = styled.div`
  padding:60px 80px 44px;position:relative;z-index:2;
  background:linear-gradient(to bottom,${({theme})=>theme.colors.mid},${({theme})=>theme.colors.darker});
  @media(max-width:900px){padding:50px 24px 36px}
`;
const HI = styled.div`max-width:1360px;margin:0 auto`;
const PageTitle = styled.h1`
  font-family:'Bebas Neue',sans-serif;
  font-size:clamp(56px,10vw,130px);line-height:.88;margin-bottom:18px;
  .accent{background:${({theme})=>theme.gradients.brand};-webkit-background-clip:text;-webkit-text-fill-color:transparent}
`;
const PageDesc = styled.p`
  font-size:15px;color:${({theme})=>theme.colors.textSecondary};
  max-width:520px;line-height:1.76;margin-bottom:32px;
`;

const Filters = styled.div`display:flex;gap:8px;flex-wrap:wrap`;
const FBtn = styled.button`
  font-family:'Space Mono',monospace;font-size:10px;letter-spacing:.2em;text-transform:uppercase;
  padding:8px 20px;
  border:1px solid ${p=>p.$a?p.$ac||'#00A3FF':'rgba(255,255,255,.1)'};
  background:${p=>p.$a?`rgba(0,163,255,.1)`:'transparent'};
  color:${p=>p.$a?p.$ac||'#00A3FF':'rgba(255,255,255,.35)'};
  clip-path:polygon(6px 0%,100% 0%,calc(100% - 6px) 100%,0% 100%);
  transition:all .3s;
  &:hover{border-color:#00A3FF;color:#00A3FF}
`;

const Section = styled.div`
  position:relative;z-index:2;
  padding:48px 80px 80px;max-width:1360px;margin:0 auto;
  @media(max-width:900px){padding:36px 24px 60px}
  @media(max-width:480px){padding:24px 16px 48px}
`;
const SectionHead = styled.div`
  display:flex;align-items:center;gap:16px;margin-bottom:28px;
`;
const SHead = styled.h2`
  font-family:'Bebas Neue',sans-serif;font-size:32px;letter-spacing:.05em;color:#fff;
`;
const PlayBadge = styled.div`
  display:flex;align-items:center;gap:6px;
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.28em;
  color:#00FF88;border:1px solid rgba(0,255,136,.25);padding:4px 12px;text-transform:uppercase;
  &::before{content:'';width:6px;height:6px;background:#00FF88;border-radius:50%;animation:${blink} 1.5s infinite;box-shadow:0 0 6px #00FF88}
`;

const Grid = styled.div`
  display:grid;
  grid-template-columns:${p=>p.$two?'repeat(2,1fr)':'repeat(3,1fr)'};
  gap:14px;
  @media(max-width:1100px){grid-template-columns:repeat(2,1fr)}
  @media(max-width:640px){grid-template-columns:1fr}
`;

/* Web game card */
const WebCard = styled(Link)`
  display:flex;flex-direction:column;
  background:${({$bg})=>$bg};
  border:1px solid rgba(255,255,255,.05);
  text-decoration:none;overflow:hidden;position:relative;
  transition:all .38s;min-height:260px;
  &::after{
    content:'';position:absolute;left:0;right:0;
    height:3px;background:linear-gradient(to right,transparent,${p=>p.$ac},transparent);
    opacity:0;z-index:3;animation:${scanLine} 2.5s linear infinite;animation-play-state:paused;
  }
  &:hover{
    border-color:${p=>p.$ac}55;transform:translateY(-5px);
    box-shadow:0 20px 50px rgba(0,0,0,.5),0 0 32px ${p=>p.$ac}18;
    &::after{animation-play-state:running;opacity:1}
  }
`;
const WCImg = styled.div`
  height:160px;background:${p=>p.$bg};display:flex;align-items:center;justify-content:center;
  font-size:68px;position:relative;overflow:hidden;flex-shrink:0;
  &::before{content:'';position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.5) 0%,transparent 60%)}
`;
const WCGlow = styled.div`
  position:absolute;inset:0;
  background:radial-gradient(ellipse 70% 50% at 50% 80%,${p=>p.$ac}18 0%,transparent 70%);
  opacity:0;transition:opacity .38s;
  ${WebCard}:hover &{opacity:1}
`;
const WCLine = styled.div`
  position:absolute;bottom:0;left:0;height:2px;width:0%;
  background:${p=>p.$ac};box-shadow:0 0 10px ${p=>p.$ac};
  transition:width .5s;
  ${WebCard}:hover &{width:100%}
`;
const WCBody = styled.div`padding:20px 22px;flex:1;display:flex;flex-direction:column`;
const WCTop  = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:10px`;
const WCStatus= styled.span`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.22em;
  color:${p=>p.$c};border:1px solid ${p=>p.$c}33;padding:3px 9px;text-transform:uppercase;
  display:flex;align-items:center;gap:5px;
  &::before{content:'';width:5px;height:5px;background:${p=>p.$c};border-radius:50%;animation:${blink} 2s infinite}
`;
const WCPlat = styled.span`
  font-family:'Space Mono',monospace;font-size:8px;letter-spacing:.15em;
  color:rgba(255,255,255,.25);border:1px solid rgba(255,255,255,.08);padding:2px 8px;text-transform:uppercase;
`;
const WCTitle= styled.h3`
  font-family:'Bebas Neue',sans-serif;font-size:34px;letter-spacing:.04em;color:#fff;
  margin-bottom:6px;transition:all .3s;
  ${WebCard}:hover &{background:${p=>`linear-gradient(135deg,#fff 40%,${p.$ac})`};-webkit-background-clip:text;-webkit-text-fill-color:transparent}
`;
const WCGenre= styled.div`font-size:11px;letter-spacing:.2em;color:rgba(255,255,255,.28);text-transform:uppercase;font-weight:600;margin-bottom:10px`;
const WCDesc = styled.p`font-size:13px;line-height:1.7;color:rgba(255,255,255,.42);flex:1;display:-webkit-box;-webkit-line-clamp:2;overflow:hidden`;
const WCPlay = styled.div`
  display:flex;align-items:center;gap:7px;margin-top:14px;
  font-family:'Space Mono',monospace;font-size:10px;letter-spacing:.22em;
  color:${p=>p.$ac};text-transform:uppercase;
  opacity:0;transform:translateY(5px);transition:all .3s;
  ${WebCard}:hover &{opacity:1;transform:translateY(0)}
  span{transition:transform .3s}
  ${WebCard}:hover span{transform:translateX(5px)}
`;

/* Regular game card */
const RegCard = styled(Link)`
  display:block;min-height:280px;
  background:${p=>p.$bg};
  border:1px solid rgba(255,255,255,.05);
  text-decoration:none;overflow:hidden;position:relative;
  transition:all .4s;display:flex;align-items:flex-end;
  &:hover{
    border-color:${p=>p.$ac}44;transform:translateY(-4px);
    box-shadow:0 18px 50px rgba(0,0,0,.5),0 0 32px ${p=>p.$ac}10;
  }
`;
const RCEmoji = styled.div`
  position:absolute;top:50%;left:50%;transform:translate(-40%,-60%);
  font-size:160px;opacity:.05;filter:grayscale(1);pointer-events:none;
  transition:opacity .4s,transform .4s;
  ${RegCard}:hover &{opacity:.1;transform:translate(-40%,-65%) scale(1.05)}
`;
const RCBottom= styled.div`
  position:absolute;bottom:0;left:0;height:2px;width:0;
  background:${p=>p.$ac};transition:width .5s;box-shadow:0 0 10px ${p=>p.$ac};
  ${RegCard}:hover &{width:100%}
`;
const RCBody = styled.div`position:relative;z-index:1;padding:28px 26px;width:100%`;
const RCStat = styled.div`display:flex;justify-content:space-between;margin-bottom:14px;flex-wrap:wrap;gap:6px`;
const RCStatus=styled.span`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.22em;
  color:${p=>p.$c};border:1px solid ${p=>p.$c}44;padding:3px 9px;text-transform:uppercase;
`;
const RCYear = styled.span`font-family:'Space Mono',monospace;font-size:9px;color:rgba(255,255,255,.2);letter-spacing:.18em`;
const RCTitle= styled.h2`
  font-family:'Bebas Neue',sans-serif;font-size:clamp(32px,4vw,50px);line-height:.9;color:#fff;margin-bottom:8px;
  transition:all .3s;
  ${RegCard}:hover &{background:${p=>`linear-gradient(135deg,#fff 40%,${p.$ac})`};-webkit-background-clip:text;-webkit-text-fill-color:transparent}
`;
const RCGenre= styled.div`font-size:11px;letter-spacing:.2em;color:rgba(255,255,255,.3);text-transform:uppercase;font-weight:600;margin-bottom:10px`;
const RCDesc = styled.p`font-size:13px;line-height:1.7;color:rgba(255,255,255,.4);display:-webkit-box;-webkit-line-clamp:2;overflow:hidden`;

const Empty = styled.div`
  grid-column:1/-1;text-align:center;padding:60px 20px;
  font-family:'Rajdhani',sans-serif;font-size:16px;letter-spacing:.2em;
  color:rgba(255,255,255,.15);text-transform:uppercase;
`;

export default function GamesPage(){
  const { t } = useTranslation();
  useDocTitle('pageTitles.games');
  const [filter,setFilter]=useState('all');

  const FILTERS = [
    {label: t('gamesPage.filterAll'),       value:'all'},
    {label: t('gamesPage.filterAvailable'), value:'web'},
    {label: t('gamesPage.filterDev'),       value:'dev'},
    {label: t('gamesPage.filterConcept'),   value:'concept'},
  ];

  const webGames = games.filter(g=>g.webGame);
  const regGames = games.filter(g=>!g.webGame);

  const filterRegular=()=>{
    if(filter==='all') return regGames;
    if(filter==='web') return [];
    if(filter==='dev') return regGames.filter(g=>g.status==='Em Desenvolvimento');
    if(filter==='concept') return regGames.filter(g=>g.status==='Conceito'||g.status==='Protótipo');
    return regGames;
  };

  const showWeb = filter==='all'||filter==='web';
  const filtered = filterRegular();

  return(
    <Page>
      <GridBg/>

      <Hero>
        <HI>
          <SectionLabel>{t('gamesPage.sectionLabel')}</SectionLabel>
          <PageTitle>
            {t('gamesPage.titleLine1')}<br/><span className="accent">{t('gamesPage.titleAccent')}</span>
          </PageTitle>
          <PageDesc>
            {t('gamesPage.desc')}
          </PageDesc>
          <Filters>
            {FILTERS.map(f=>(
              <FBtn key={f.value} $a={filter===f.value} onClick={()=>setFilter(f.value)}>
                {f.label}
              </FBtn>
            ))}
          </Filters>
        </HI>
      </Hero>

      <Section>
        {showWeb&&(
          <>
            <SectionHead>
              <SHead>{t('gamesPage.sectionPlay')}</SHead>
              <PlayBadge>{t('gamesPage.availableBrowser')}</PlayBadge>
            </SectionHead>
            <Grid style={{marginBottom:56}}>
              {webGames.map((g,i)=>(
                <AnimatedSection key={g.id} delay={i*80}>
                  <WebCard to={g.route} $bg={g.gradient} $ac={g.accentColor}>
                    <WCImg $bg={g.gradient}>
                      <span>{g.emoji}</span>
                      <WCGlow $ac={g.accentColor}/>
                      <WCLine $ac={g.accentColor}/>
                    </WCImg>
                    <WCBody>
                      <WCTop>
                        <WCStatus $c={g.statusColor}>{g.status}</WCStatus>
                        <WCPlat>{t('gamesPage.webPlatform')}</WCPlat>
                      </WCTop>
                      <WCTitle $ac={g.accentColor}>{g.title}</WCTitle>
                      <WCGenre>{g.genre}</WCGenre>
                      <WCDesc>{g.description}</WCDesc>
                      <WCPlay $ac={g.accentColor}>{t('gamesPage.playNow')} <span>→</span></WCPlay>
                    </WCBody>
                  </WebCard>
                </AnimatedSection>
              ))}
            </Grid>
          </>
        )}

        {filtered.length>0&&(
          <>
            <SectionHead style={{marginTop: showWeb ? 0 : 0}}>
              <SHead>{t('gamesPage.sectionInProd')}</SHead>
            </SectionHead>
            <Grid $two={filtered.length<=2}>
              {filtered.map((g,i)=>(
                <AnimatedSection key={g.id} delay={i*80}>
                  <RegCard to={`/games/${g.id}`} $bg={g.gradient} $ac={g.accentColor}>
                    <RCEmoji>{g.emoji}</RCEmoji>
                    <RCBottom $ac={g.accentColor}/>
                    <RCBody>
                      <RCStat>
                        <RCStatus $c={g.statusColor}>{g.status}</RCStatus>
                        <RCYear>{g.year}</RCYear>
                      </RCStat>
                      <RCTitle $ac={g.accentColor}>{g.title}</RCTitle>
                      <RCGenre>{g.genre}</RCGenre>
                      <RCDesc>{g.description}</RCDesc>
                    </RCBody>
                  </RegCard>
                </AnimatedSection>
              ))}
            </Grid>
          </>
        )}

        {!showWeb&&filtered.length===0&&(
          <Empty>{t('gamesPage.empty')}</Empty>
        )}
      </Section>

      <Footer/>
    </Page>
  );
}
